import client from "@/client/publicApiClient.ts";
import { useQuery, queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import type { AcceptVerifyIdentityEmailQueryResponse, AcceptVerifyIdentityEmailPathParams } from "../types/AcceptVerifyIdentityEmail";
import type { QueryObserverOptions, UseQueryResult, QueryKey, UseSuspenseQueryOptions, UseSuspenseQueryResult } from "@tanstack/react-query";

 type AcceptVerifyIdentityEmailClient = typeof client<AcceptVerifyIdentityEmailQueryResponse, never, never>;
type AcceptVerifyIdentityEmail = {
    data: AcceptVerifyIdentityEmailQueryResponse;
    error: never;
    request: never;
    pathParams: AcceptVerifyIdentityEmailPathParams;
    queryParams: never;
    headerParams: never;
    response: AcceptVerifyIdentityEmailQueryResponse;
    client: {
        parameters: Partial<Parameters<AcceptVerifyIdentityEmailClient>[0]>;
        return: Awaited<ReturnType<AcceptVerifyIdentityEmailClient>>;
    };
};
export const acceptVerifyIdentityEmailQueryKey = (requestedEmailVerifyToken: AcceptVerifyIdentityEmailPathParams["requestedEmailVerifyToken"]) => [{ url: "/api/verify-email/:requestedEmailVerifyToken", params: { requestedEmailVerifyToken: requestedEmailVerifyToken } }] as const;
export type AcceptVerifyIdentityEmailQueryKey = ReturnType<typeof acceptVerifyIdentityEmailQueryKey>;
export function acceptVerifyIdentityEmailQueryOptions(requestedEmailVerifyToken: AcceptVerifyIdentityEmailPathParams["requestedEmailVerifyToken"], options: AcceptVerifyIdentityEmail["client"]["parameters"] = {}) {
    const queryKey = acceptVerifyIdentityEmailQueryKey(requestedEmailVerifyToken);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<AcceptVerifyIdentityEmail["data"], AcceptVerifyIdentityEmail["error"]>({
                method: "get",
                url: `/api/verify-email/${requestedEmailVerifyToken}`,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @link /api/verify-email/:requestedEmailVerifyToken
 */
export function useAcceptVerifyIdentityEmail<TData = AcceptVerifyIdentityEmail["response"], TQueryData = AcceptVerifyIdentityEmail["response"], TQueryKey extends QueryKey = AcceptVerifyIdentityEmailQueryKey>(requestedEmailVerifyToken: AcceptVerifyIdentityEmailPathParams["requestedEmailVerifyToken"], options: {
    query?: Partial<QueryObserverOptions<AcceptVerifyIdentityEmail["response"], AcceptVerifyIdentityEmail["error"], TData, TQueryData, TQueryKey>>;
    client?: AcceptVerifyIdentityEmail["client"]["parameters"];
} = {}): UseQueryResult<TData, AcceptVerifyIdentityEmail["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? acceptVerifyIdentityEmailQueryKey(requestedEmailVerifyToken);
    const query = useQuery({
        ...acceptVerifyIdentityEmailQueryOptions(requestedEmailVerifyToken, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseQueryResult<TData, AcceptVerifyIdentityEmail["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}
export const acceptVerifyIdentityEmailSuspenseQueryKey = (requestedEmailVerifyToken: AcceptVerifyIdentityEmailPathParams["requestedEmailVerifyToken"]) => [{ url: "/api/verify-email/:requestedEmailVerifyToken", params: { requestedEmailVerifyToken: requestedEmailVerifyToken } }] as const;
export type AcceptVerifyIdentityEmailSuspenseQueryKey = ReturnType<typeof acceptVerifyIdentityEmailSuspenseQueryKey>;
export function acceptVerifyIdentityEmailSuspenseQueryOptions(requestedEmailVerifyToken: AcceptVerifyIdentityEmailPathParams["requestedEmailVerifyToken"], options: AcceptVerifyIdentityEmail["client"]["parameters"] = {}) {
    const queryKey = acceptVerifyIdentityEmailSuspenseQueryKey(requestedEmailVerifyToken);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<AcceptVerifyIdentityEmail["data"], AcceptVerifyIdentityEmail["error"]>({
                method: "get",
                url: `/api/verify-email/${requestedEmailVerifyToken}`,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @link /api/verify-email/:requestedEmailVerifyToken
 */
export function useAcceptVerifyIdentityEmailSuspense<TData = AcceptVerifyIdentityEmail["response"], TQueryKey extends QueryKey = AcceptVerifyIdentityEmailSuspenseQueryKey>(requestedEmailVerifyToken: AcceptVerifyIdentityEmailPathParams["requestedEmailVerifyToken"], options: {
    query?: Partial<UseSuspenseQueryOptions<AcceptVerifyIdentityEmail["response"], AcceptVerifyIdentityEmail["error"], TData, TQueryKey>>;
    client?: AcceptVerifyIdentityEmail["client"]["parameters"];
} = {}): UseSuspenseQueryResult<TData, AcceptVerifyIdentityEmail["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? acceptVerifyIdentityEmailSuspenseQueryKey(requestedEmailVerifyToken);
    const query = useSuspenseQuery({
        ...acceptVerifyIdentityEmailSuspenseQueryOptions(requestedEmailVerifyToken, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseSuspenseQueryResult<TData, AcceptVerifyIdentityEmail["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}